<template>
  <Teleport to="body">
    <div class="search-overlay" :class="{ open: visible }" @click.self="close">
      <div class="search-dialog">
        <div class="search-input-wrap">
          <svg class="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          <input
            ref="inputRef"
            v-model="query"
            type="text"
            :placeholder="locale === 'zh' ? '搜索页面、设备、案例...' : 'Search pages, equipment, cases...'"
            @input="onInput"
            @keydown.escape="close"
            @keydown.down.prevent="moveDown"
            @keydown.up.prevent="moveUp"
            @keydown.enter.prevent="selectActive"
            autocomplete="off"
          />
          <button class="search-close" @click="close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </button>
        </div>
        <div class="search-results" v-if="query.trim().length >= 1">
          <div class="search-hint" v-if="results.length === 0 && searched">
            {{ locale === 'zh' ? '未找到匹配结果' : 'No results found' }}
          </div>
          <div class="search-hint" v-else-if="results.length === 0">
            {{ locale === 'zh' ? '请输入关键词开始搜索' : 'Type to search...' }}
          </div>
          <div
            v-for="(r, i) in results"
            :key="i"
            class="search-result-item"
            :class="{ active: activeIndex === i }"
            @click="navigate(r)"
            @mouseenter="activeIndex = i"
          >
            <span class="result-icon">{{ r.icon }}</span>
            <div class="result-info">
              <span class="result-title">{{ r.title }}</span>
              <span class="result-sub" v-if="r.sub">{{ r.sub }}</span>
            </div>
            <span class="result-badge" v-if="r.badge">{{ r.badge }}</span>
            <span class="result-arrow">→</span>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

const { locale } = useI18n()
const router = useRouter()

const props = defineProps({ visible: Boolean })
const emit = defineEmits(['close'])

const query = ref('')
const searched = ref(false)
const activeIndex = ref(-1)
const inputRef = ref(null)

// ==================== 搜索索引 ====================
const searchIndex = computed(() => {
  const zh = locale.value === 'zh'
  const items = []

  // 页面路由
  items.push({ title: zh ? '首页' : 'Home', sub: zh ? '网站首页' : 'Website homepage', icon: '🏠', route: '/', badge: zh ? '页面' : 'Page' })
  items.push({ title: zh ? '关于我们' : 'About Us', sub: zh ? '公司介绍与发展历程' : 'Company introduction & history', icon: '🏢', route: '/about', badge: zh ? '页面' : 'Page' })
  items.push({ title: zh ? '联系我们' : 'Contact Us', sub: zh ? '联系方式与留言表单' : 'Contact info & inquiry form', icon: '📬', route: '/about#contact', badge: zh ? '页面' : 'Page' })

  // SLM 设备列表
  const eqs = [
    { id: 's180', name: 'S180', size: '180mm', cat: 'small' },
    { id: 's350', name: 'S350', size: '350mm', cat: 'medium' },
    { id: 's420', name: 'S420', size: '420mm', cat: 'large' },
    { id: 's460', name: 'S460', size: '460mm', cat: 'large' },
    { id: 'm460', name: 'M460', size: '460mm', cat: 'large' },
    { id: 'm600', name: 'M600', size: '600mm', cat: 'large' },
    { id: 's610', name: 'S610', size: '610mm', cat: 'xlarge' },
    { id: 's650', name: 'S650', size: '650mm', cat: 'xlarge' },
    { id: 's850', name: 'S850', size: '850mm', cat: 'xlarge' },
    { id: 's1300', name: 'S1300', size: '1300mm', cat: 'xlarge' },
    { id: 's1500', name: 'S1500', size: '1500mm', cat: 'xlarge' },
    { id: 's2500', name: 'S2500', size: '2500mm', cat: 'xlarge' },
    { id: 'w3500', name: 'W3500', size: '3500mm', cat: 'xlarge' },
  ]
  eqs.forEach(e => {
    items.push({
      title: `CR-${e.name}`,
      sub: zh ? `SLM金属3D打印设备 · ${e.size}` : `SLM Metal 3D Printer · ${e.size}`,
      icon: '🖨️',
      route: `/equipment/${e.id}`,
      badge: zh ? '设备' : 'Equipment',
    })
  })

  // 应用产品
  const prods = zh
    ? [
      { title: '医疗/齿科', sub: '义齿、牙冠、骨科植入物3D打印', route: '/products', icon: '🦷' },
      { title: '教育/科研', sub: '高校科研设备与STEAM创新教育', route: '/products', icon: '🎓' },
      { title: '鞋底模具', sub: '金属3D打印鞋模，随形冷却水道一体化', route: '/products', icon: '👟' },
      { title: '注塑/压铸模具', sub: 'SLM打印高性能注塑与压铸模具', route: '/products', icon: '⚙️' },
      { title: '精密结构件', sub: '复杂内腔、点阵轻量化一体化成型', route: '/products', icon: '🔧' },
    ]
    : [
      { title: 'Medical/Dental', sub: 'Dental crowns, implants, orthopedic 3D printing', route: '/products', icon: '🦷' },
      { title: 'Education/Research', sub: 'University equipment & STEAM education', route: '/products', icon: '🎓' },
      { title: 'Shoe Sole Molds', sub: 'Metal 3D printed shoe molds with conformal cooling', route: '/products', icon: '👟' },
      { title: 'Injection/Die-Casting Molds', sub: 'SLM high-performance injection & die-casting molds', route: '/products', icon: '⚙️' },
      { title: 'Precision Parts', sub: 'Complex cavities, lattice lightweight, integrated forming', route: '/products', icon: '🔧' },
    ]
  prods.forEach(p => items.push({ ...p, badge: zh ? '产品' : 'Product' }))

  // 案例展示
  const cases = [
    { zhTitle: '火箭栅格舵增材制造', enTitle: 'Rocket Grid Rudder AM', zhSub: '航空航天 · W3500等离子束电弧增材', enSub: 'Aerospace · W3500 Plasma Arc AM', route: '/projects', icon: '🚀' },
    { zhTitle: '航空发动机关键零部件', enTitle: 'Aero Engine Critical Parts', zhSub: '航空航天 · S2500超大尺寸SLM', enSub: 'Aerospace · S2500 Ultra-large SLM', route: '/projects', icon: '✈️' },
    { zhTitle: '无人机点阵加芯机翼', enTitle: 'UAV Lattice-Cored Wing', zhSub: '航空航天 · S650轻量化结构', enSub: 'Aerospace · S650 Lightweight', route: '/projects', icon: '🛩️' },
    { zhTitle: '3D打印轮胎模具', enTitle: '3D Printed Tire Mold', zhSub: '汽车交通 · S420随形冷却', enSub: 'Automotive · S420 Conformal Cooling', route: '/projects', icon: '🚗' },
    { zhTitle: '金属鞋底模具批量生产', enTitle: 'Shoe Sole Mold Mass Production', zhSub: '模具制造 · M600 8激光专机', enSub: 'Mold · M600 8-Laser Machine', route: '/projects', icon: '👞' },
    { zhTitle: '齿科植入物定制生产', enTitle: 'Dental Implant Custom Production', zhSub: '医疗健康 · S180绿光高精度', enSub: 'Medical · S180 Green Laser', route: '/projects', icon: '🦷' },
    { zhTitle: '核电装备关键部件', enTitle: 'Nuclear Equipment Components', zhSub: '工业制造 · S610大尺寸打印', enSub: 'Industrial · S610 Large Format', route: '/projects', icon: '⚡' },
    { zhTitle: '海工船舶大型构件', enTitle: 'Marine & Shipbuilding', zhSub: '工业制造 · S610米级构件', enSub: 'Industrial · S610 Meter-scale', route: '/projects', icon: '🚢' },
  ]
  cases.forEach(c => {
    items.push({
      title: zh ? c.zhTitle : c.enTitle,
      sub: zh ? c.zhSub : c.enSub,
      icon: c.icon,
      route: c.route,
      badge: zh ? '案例' : 'Case',
    })
  })

  // 工厂资质
  items.push({ title: zh ? '工厂与资质' : 'Factory & Certifications', sub: zh ? '40,000m² 车间面积 · 生产基地与体系认证' : '40,000m² Workshop · Production bases & certifications', icon: '🏭', route: '/factory', badge: zh ? '页面' : 'Page' })
  items.push({ title: zh ? 'ISO 9001:2015 质量管理体系' : 'ISO 9001:2015 QMS', sub: zh ? '质量体系认证' : 'Quality Management System', icon: '📜', route: '/factory', badge: zh ? '认证' : 'Cert' })
  items.push({ title: 'CE Certification', sub: zh ? '欧盟CE认证' : 'EU CE Certification', icon: '📜', route: '/factory', badge: zh ? '认证' : 'Cert' })

  // 新闻
  items.push({ title: zh ? '新闻动态' : 'News', sub: zh ? 'TCT亚洲展及最新产品发布' : 'TCT Asia & latest product launches', icon: '📰', route: '/news', badge: zh ? '页面' : 'Page' })

  // 核心优势
  items.push({ title: zh ? '黑灯工厂自动化' : 'Dark Factory Automation', sub: zh ? '24/7无人化智能产线' : '24/7 unmanned intelligent production', icon: '🌙', route: '/', badge: zh ? '首页' : 'Home' })
  items.push({ title: zh ? '材料利用率 >95%' : 'Material Usage >95%', sub: zh ? '增材制造净成形对比传统切削10%-40%' : 'Net-shape AM vs traditional 10%-40%', icon: '♻️', route: '/', badge: zh ? '优势' : 'Adv.' })

  return items
})

const results = ref([])

function fuzzyMatch(item, q) {
  const lower = q.toLowerCase()
  const fields = [item.title, item.sub || '', item.badge || '']
  return fields.some(f => f.toLowerCase().includes(lower))
}

function onInput() {
  const q = query.value.trim()
  if (q.length < 1) {
    results.value = []
    searched.value = false
    activeIndex.value = -1
    return
  }
  const matched = searchIndex.value.filter(item => fuzzyMatch(item, q))
  results.value = matched.slice(0, 10)
  searched.value = true
  activeIndex.value = results.value.length > 0 ? 0 : -1
}

function moveDown() {
  if (results.value.length === 0) return
  activeIndex.value = (activeIndex.value + 1) % results.value.length
}

function moveUp() {
  if (results.value.length === 0) return
  activeIndex.value = (activeIndex.value - 1 + results.value.length) % results.value.length
}

function selectActive() {
  if (activeIndex.value >= 0 && results.value[activeIndex.value]) {
    navigate(results.value[activeIndex.value])
  }
}

function navigate(r) {
  close()
  router.push(r.route)
}

function close() {
  emit('close')
}

watch(() => props.visible, async (v) => {
  if (v) {
    query.value = ''
    results.value = []
    searched.value = false
    activeIndex.value = -1
    await nextTick()
    inputRef.value?.focus()
  }
})
</script>

<style scoped>
.search-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: transparent;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  display: flex;
  justify-content: center;
  padding: 120px 20px 40px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.25s;
}
.search-overlay.open {
  opacity: 1;
  pointer-events: auto;
}
.search-dialog {
  width: 100%;
  max-width: 600px;
  max-height: calc(100vh - 160px);
  display: flex;
  flex-direction: column;
  background: rgba(8,15,26,0.4);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.5);
  overflow: hidden;
}
.search-input-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.search-icon {
  color: var(--color-text-muted);
  flex-shrink: 0;
}
.search-input-wrap input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: var(--color-heading);
  font-size: 1.05rem;
  font-family: inherit;
  padding: 4px 0;
}
.search-input-wrap input::placeholder {
  color: var(--color-text-muted);
}
.search-close {
  background: none;
  border: none;
  color: var(--color-text-muted);
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
}
.search-close:hover {
  color: var(--color-heading);
  background: var(--color-nav-hover-bg);
}
.search-results {
  overflow-y: auto;
  max-height: 420px;
  padding: 8px;
}
.search-hint {
  padding: 24px;
  text-align: center;
  color: var(--color-text-muted);
  font-size: 0.9rem;
}
.search-result-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
}
.search-result-item:hover,
.search-result-item.active {
  background: var(--color-nav-hover-bg);
}
.result-icon {
  font-size: 1.2rem;
  flex-shrink: 0;
  width: 32px;
  text-align: center;
}
.result-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.result-title {
  font-size: 0.92rem;
  color: var(--color-heading);
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.result-sub {
  font-size: 0.78rem;
  color: var(--color-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.result-badge {
  font-size: 0.7rem;
  padding: 2px 8px;
  border-radius: 10px;
  background: rgba(0,212,255,0.1);
  color: var(--color-primary);
  font-weight: 500;
  flex-shrink: 0;
}
.result-arrow {
  color: var(--color-text-muted);
  font-size: 0.8rem;
  flex-shrink: 0;
}
</style>
